/**
 * Phase Configuration
 *
 * Centralized phase and status mappings for all sports scoring components.
 * Separated from scoringConfig.js to avoid circular dependencies.
 *
 * ============================================================================
 * STANDARDIZED 4-PHASE MODEL
 * ============================================================================
 * All sports follow this basic flow:
 * 1. setup - Configure game settings
 * 2. player_selection - Select players/teams
 * 3. active - Live scoring
 * 4. completed - View results and share
 */

/**
 * Base STATUS_TO_PHASE mapping (shared across all sports)
 * Maps database status values to UI phase names
 */
const BASE_STATUS_TO_PHASE = {
  'setup': 'setup',
  'player_selection': 'teams',
  'active': 'scoring',
  'completed': 'completed',
  // Legacy support
  'pending': 'teams',
  'in_progress': 'scoring'
};

/**
 * Sport-specific STATUS_TO_PHASE mappings
 * Each sport can extend the base mapping with sport-specific statuses
 */
export const STATUS_TO_PHASE_MAPPINGS = {
  // Ping Pong - Uses standard 4-phase model
  pingpong: {
    ...BASE_STATUS_TO_PHASE
  },

  // Cricket - Uses standard statuses, but has cricket-specific UI phases
  cricket: {
    ...BASE_STATUS_TO_PHASE
    // Note: Cricket UI has 'innings-break' phase, but this is managed locally
    // in the component. The database status remains 'in_progress' throughout.
  },

  // Tennis - May include serving-order phase
  tennis: {
    ...BASE_STATUS_TO_PHASE,
    'serving-order': 'serving-order', // Optional intermediate phase for serve selection
    'scoring': 'scoring'              // Legacy
  },

  // Pickleball - Uses standard 4-phase model
  pickleball: {
    ...BASE_STATUS_TO_PHASE,
    'teams': 'teams',                 // Legacy status
    'scoring': 'scoring'              // Legacy status
  },

  // Badminton - Uses standard 4-phase model
  badminton: {
    ...BASE_STATUS_TO_PHASE
  },

  // Basketball - Uses standard 4-phase model
  basketball: {
    ...BASE_STATUS_TO_PHASE
  },

  // Poker - Uses standard 4-phase model with poker-specific phases
  poker: {
    ...BASE_STATUS_TO_PHASE,
    'finished': 'completed'           // Legacy status
  }
};

/**
 * Get STATUS_TO_PHASE mapping for a specific sport
 * @param {string} sport - Sport name (e.g., 'pingpong', 'cricket', 'tennis')
 * @returns {object} Status to phase mapping
 */
export const getStatusToPhaseMapping = (sport) => {
  return STATUS_TO_PHASE_MAPPINGS[sport] || BASE_STATUS_TO_PHASE;
};

/**
 * Standard Status Values
 * All sports should use these standard status values in their backend schemas
 */
export const STANDARD_STATUSES = ['setup', 'player_selection', 'active', 'completed'];

/**
 * Legacy Status Values (for backward compatibility)
 * These should be supported but not used for new games
 */
export const LEGACY_STATUSES = ['pending', 'in_progress', 'scoring', 'finished'];

/**
 * All Valid Statuses (standard + legacy + sport-specific)
 */
export const ALL_VALID_STATUSES = {
  pingpong: [...STANDARD_STATUSES, ...LEGACY_STATUSES],
  badminton: [...STANDARD_STATUSES, ...LEGACY_STATUSES],
  cricket: [...STANDARD_STATUSES, 'toss', 'innings-break', 'in_progress'],
  tennis: [...STANDARD_STATUSES, 'serving-order', 'in_progress', 'scoring'],
  pickleball: [...STANDARD_STATUSES, 'in_progress', 'scoring'],
  basketball: [...STANDARD_STATUSES, ...LEGACY_STATUSES],
  poker: [...STANDARD_STATUSES, 'finished', ...LEGACY_STATUSES]
};

/**
 * Placeholder Player Constants
 * Used to satisfy database NOT NULL constraints during setup phase
 */
export const PLACEHOLDER_PLAYER_A = { type: 'user', id: -1, name: 'Player A' };
export const PLACEHOLDER_PLAYER_B = { type: 'user', id: -2, name: 'Player B' };
export const PLACEHOLDER_TEAM_1 = { name: 'Team 1', id: -1, players: [] };
export const PLACEHOLDER_TEAM_2 = { name: 'Team 2', id: -2, players: [] };

/**
 * Check if a player/team is a placeholder
 * @param {object} entity - Player or team object
 * @returns {boolean} True if placeholder (id < 0 or empty players array)
 */
export const isPlaceholder = (entity) => {
  if (!entity) return true;
  if (entity.id < 0) return true;
  if (Array.isArray(entity.players) && entity.players.length === 0) return true;
  return false;
};

/**
 * Get active game statuses (for finding in-progress games)
 * @param {string} sport - Sport name
 * @returns {string[]} Array of status values that indicate an active game
 */
export const getActiveStatuses = (sport) => {
  const baseActive = ['setup', 'player_selection', 'active'];

  // Add sport-specific active statuses
  const sportSpecific = {
    cricket: [...baseActive, 'toss', 'innings-break'],
    tennis: [...baseActive, 'serving-order'],
    // Other sports use base active statuses
  };

  const active = sportSpecific[sport] || baseActive;

  // Add legacy statuses
  return [...active, 'pending', 'in_progress', 'scoring'];
};

/**
 * Phase Display Names
 * Human-readable names for each phase
 */
export const PHASE_DISPLAY_NAMES = {
  'setup': 'Game Settings',
  'teams': 'Player Selection',
  'serving-order': 'Serving Order',
  'scoring': 'Live Scoring',
  'innings-break': 'Innings Break',
  'completed': 'Results'
};

export default {
  getStatusToPhaseMapping,
  getActiveStatuses,
  isPlaceholder,
  STANDARD_STATUSES,
  LEGACY_STATUSES,
  PHASE_DISPLAY_NAMES,
  STATUS_TO_PHASE_MAPPINGS,
  ALL_VALID_STATUSES
};
